import { useEffect, useRef } from "react";
import { spline } from "@georgedoescode/spline";
import { createNoise2D } from "simplex-noise";

export default function BlobSVG() {
  const pathRef = useRef(null);
  const noiseStepRef = useRef(0.005);
  const rafRef = useRef(null);
  const isVisibleRef = useRef(true);

  useEffect(() => {
    const path = pathRef.current;
    if (!path) return;

    const noise2D = createNoise2D();
    const points = createPoints();

    let lastTime = performance.now();
    const observer = new IntersectionObserver(
      ([entry]) => {
        isVisibleRef.current = entry.isIntersecting;
      },
      { threshold: 0.1 }
    );

    observer.observe(path);

    const animate = (time) => {
      const delta = (time - lastTime) / 16.67;
      lastTime = time;
      if (isVisibleRef.current) {
        path.setAttribute("d", spline(points, 1, true));

        for (let i = 0; i < points.length; i++) {
          const p = points[i];

          const nx = noise2D(p.noiseOffsetX, p.noiseOffsetX);
          const ny = noise2D(p.noiseOffsetY, p.noiseOffsetY);

          p.x = lerp(p.originX - 20, p.originX + 20, (nx + 1) / 2);
          p.y = lerp(p.originY - 20, p.originY + 20, (ny + 1) / 2);

          p.noiseOffsetX += noiseStepRef.current * delta;
          p.noiseOffsetY += noiseStepRef.current * delta;
        }
      }

      rafRef.current = requestAnimationFrame(animate);
    };

    rafRef.current = requestAnimationFrame(animate);

    return () => {
      observer.disconnect();
      cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return (
    <svg
      className="blob"
      viewBox="0 0 200 200"
      onMouseEnter={() => (noiseStepRef.current = 0.01)}
      onMouseLeave={() => (noiseStepRef.current = 0.005)}
    >
      <path ref={pathRef} fill="#ffffff" />
    </svg>
  );
}

function lerp(a, b, t) {
  return a + (b - a) * t;
}

function createPoints() {
  const points = [];
  const count = 6;
  const step = (Math.PI * 2) / count;
  const radius = 75;

  for (let i = 0; i < count; i++) {
    const theta = i * step;

    const x = 100 + Math.cos(theta) * radius;
    const y = 100 + Math.sin(theta) * radius;

    points.push({
      x,
      y,
      originX: x,
      originY: y,
      noiseOffsetX: Math.random() * 1000,
      noiseOffsetY: Math.random() * 1000,
    });
  }

  return points;
}
